import { Fragment, useState } from 'react';
import { Row, Col, Button, Input } from 'reactstrap';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import { Filter } from '../../../services/filters';
import { renderIcon } from '../../../shared/components/icons';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material';
import { Loading } from '~/shared/components/Loading';
import React from 'react';
import { SimpleFundRequest } from '~/types/fundrequests';
import { toUSD } from '~/shared/utils';

type TableContainerType = {
    columns: any;
    data: any;
    showPaginationComponents?: boolean;
    showFilterComponents?: boolean;
    renderRowSubComponent?: Function;
    showHeaders?: boolean;
    sort?: boolean;
    NoDataCard?: JSX.Element;
    page?: Number;
    pageCount?: Number;
    setPage?: any;
    setPageSize?: any;
    pageSize?: any;
    tableLoader?: boolean;
    isServerSidePagination?: boolean;
    fullWidth?: boolean;
    isRequestTable?: boolean;
    onSortChange?: (columnId: string, direction: 'asc' | 'desc') => void;
    currentSortBy?: string;
    currentSortOrder?: string;
};

type ColumnSort = {
    id: string;
    desc: boolean;
};

type SortingState = ColumnSort[];

interface ColumnFilter {
    id: string;
    value: unknown;
}
type ColumnFiltersState = ColumnFilter[];

const useStyles = makeStyles((_theme: Theme) => ({
    paginationRow: {
        maxWidth: '1000px',
        margin: '0 auto',
    },
    pageInfo: {
        marginTop: 7,
    },
    pageSizeInput: {
        width: 70,
    },
    timeCell: {
        color: '#949599',
        fontSize: '12px',
    },
    icon: {
        fontSize: '18px',
    },
    updateCell: {
        maxWidth: '1136px',
    },
}));

const TableContainer = ({
    columns,
    data,
    showPaginationComponents,
    showFilterComponents,
    showHeaders = false,
    sort = true,
    NoDataCard,
    page = 1,
    pageCount = 1,
    tableLoader = false,
    setPage = () => { },
    setPageSize = () => { },
    pageSize = 20,
    isServerSidePagination = false,
    fullWidth = false,
    isRequestTable = false,
    onSortChange,
    currentSortBy,
    currentSortOrder,
}: TableContainerType) => {
    const classes = useStyles();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const table = useReactTable({
        columns,
        data,
        manualPagination: isServerSidePagination,
        initialState: {
            pagination: {
                pageSize: 20,
            },
        },
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const onChangeInInput = (event: any) => {
        const page = event.target.value ? Number(event.target.value) - 1 : 0;
        table.setPageIndex(page);
    };

    const PaginationComponents = () => {
        if (!showPaginationComponents) {
            return <></>;
        }

        return isServerSidePagination ? (
            <Row className={classes.paginationRow}>
                <Col xs={6} lg={3}>
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                    >
                        {'<<'}
                    </Button>
                    &nbsp;
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                    >
                        {'<'}
                    </Button>
                </Col>
                <Col lg={3} className={`${classes.pageInfo} d-none d-lg-block`}>
                    Page{' '}
                    <strong>
                        {page} of {pageCount}
                    </strong>
                </Col>
                <Col lg={3} className='d-none d-lg-block'>
                    <Input
                        type='select'
                        bsSize='sm'
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {[20, 50, 100, 200].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </Input>
                </Col>
                <Col xs={6} lg={3} className='text-end'>
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => setPage(page + 1)}
                        disabled={page >= pageCount}
                    >
                        {'>'}
                    </Button>
                    &nbsp;
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => setPage(pageCount)}
                        disabled={page === pageCount}
                    >
                        {'>>'}
                    </Button>
                </Col>
            </Row>
        ) : (
            <Row className={classes.paginationRow}>
                <Col xs={6} lg={3}>
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {'<<'}
                    </Button>
                    &nbsp;
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {'<'}
                    </Button>
                </Col>
                <Col lg={2} className={`${classes.pageInfo} d-none d-lg-block`}>
                    Page{' '}
                    <strong>
                        {table.getState().pagination?.pageIndex + 1} of{' '}
                        {table.getPageCount()}
                    </strong>
                </Col>
                <Col lg={2} className='d-none d-lg-block'>
                    <Input
                        type='number'
                        bsSize='sm'
                        min={1}
                        className={classes.pageSizeInput}
                        max={table.getPageCount()}
                        defaultValue={
                            table.getState().pagination?.pageIndex + 1
                        }
                        onChange={onChangeInInput}
                    ></Input>
                </Col>
                <Col lg={2} className='d-none d-lg-block'>
                    <Input
                        type='select'
                        bsSize='sm'
                        value={table.getState().pagination.pageSize}
                        onChange={(e) =>
                            table.setPageSize(Number(e.target.value))
                        }
                    >
                        {[10, 15, 20, 25, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </Input>
                </Col>
                <Col xs={6} lg={3} className='text-end'>
                    <Button
                        color='light'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {'>'}
                    </Button>
                    &nbsp;
                    <Button
                        color='light'
                        size='sm'
                        onClick={() =>
                            table.setPageIndex(table.getPageCount() - 1)
                        }
                        disabled={!table.getCanNextPage()}
                    >
                        {'>>'}
                    </Button>
                </Col>
            </Row>
        );
    };

    const getTotalAmount = (fundRequests: SimpleFundRequest[]) => {
        let totalAmount = 0;
        fundRequests.forEach((f) => {
            const amount = Number(f.request_amount) || 0;
            totalAmount += amount;
        });

        return totalAmount;
    };

    const headers = table.getHeaderGroups();

    if (!headers) {
        return <Loading />;
    }

    return (
        <Fragment>
            <Table className='table' style={{ tableLayout: 'fixed', width: '100%' }}>
                {data?.length > 0 && (
                    <Thead>
                        <Tr>
                            {headers[0].headers.map((header) => {
                                // For server-side sorting, use currentSortBy/currentSortOrder
                                // For client-side sorting, use React Table's internal state
                                const isSorted = isServerSidePagination && onSortChange
                                    ? currentSortBy === header.column.id
                                    : header.column.getIsSorted();
                                const isDesc = isServerSidePagination && onSortChange
                                    ? currentSortOrder === 'desc'
                                    : header.column.getIsSorted() === 'desc';
                                const isAsc = isServerSidePagination && onSortChange
                                    ? currentSortOrder === 'asc'
                                    : header.column.getIsSorted() === 'asc';

                                const handleHeaderClick = () => {
                                    if (onSortChange && isServerSidePagination) {
                                        // For server-side sorting
                                        const direction = isSorted && isAsc ? 'desc' : 'asc';
                                        onSortChange(header.column.id, direction);
                                    } else {
                                        // For client-side sorting
                                        header.column.toggleSorting(!isDesc);
                                    }
                                };

                                return (
                                    <Th
                                        key={header.id}
                                        style={{
                                            width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                                            minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : 'auto',
                                            maxWidth: header.column.columnDef.maxSize ? `${header.column.columnDef.maxSize}px` : 'auto',
                                        }}
                                    >
                                        <div
                                            style={{
                                                cursor: sort && header.column.columnDef.enableSorting ? 'pointer' : 'default',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onClick={sort && header.column.columnDef.enableSorting ? handleHeaderClick : undefined}
                                        >
                                            {showHeaders &&
                                                !header.isPlaceholder &&
                                                flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            {sort && header.column.columnDef.enableSorting && (
                                                <span className={classes.icon}>
                                                    {isSorted ? (
                                                        isAsc ? renderIcon('arrow_up') : renderIcon('arrow_down')
                                                    ) : (
                                                        renderIcon('double_arrow')
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        {showFilterComponents && (
                                            <Filter
                                                column={header.column}
                                                table={table}
                                            />
                                        )}
                                    </Th>
                                );
                            })}
                        </Tr>
                    </Thead>
                )}

                <React.Fragment>
                    {tableLoader ? (
                        <Tbody>
                            <Tr>
                                <Td colSpan={headers?.[0]?.headers?.length}>
                                    <Loading fullscreen={false} />
                                </Td>
                            </Tr>
                        </Tbody>
                    ) : data?.length > 0 ? (
                        <Tbody>
                            {table.getRowModel().rows.map((row) => {
                                const cells = row.getAllCells();
                                return (
                                    <Fragment key={row.id}>
                                        <Tr>
                                            {cells.map((cell) => {
                                                const val =
                                                    cell.getValue() as string;
                                                if (
                                                    cell.column.id === 'update'
                                                ) {
                                                    return (
                                                        <Td
                                                            key={cell.column.id}
                                                            className={`fullwidth ${classes.updateCell}`}
                                                        >
                                                            {/* <UpdateCell value={val} /> */}
                                                            {val}
                                                        </Td>
                                                    );
                                                } else if (
                                                    cell.column.id === 'date'
                                                ) {
                                                    const [
                                                        date,
                                                        time,
                                                        meridiem,
                                                    ] = val.split(' ');
                                                    return (
                                                        <Td
                                                            key={cell.column.id}
                                                            style={{ verticalAlign: 'top' }}
                                                        >
                                                            <div>{date}</div>
                                                            <div>
                                                                <span
                                                                    className={
                                                                        classes.timeCell
                                                                    }
                                                                >
                                                                    {time}{' '}
                                                                    {meridiem}
                                                                </span>
                                                            </div>
                                                        </Td>
                                                    );
                                                } else {
                                                    return (
                                                        <Td
                                                            key={cell.column.id}
                                                            style={{ verticalAlign: 'top' }}
                                                        >
                                                            {val}
                                                        </Td>
                                                    );
                                                }
                                            })}
                                        </Tr>
                                    </Fragment>
                                );
                            })}
                            {isRequestTable && (
                                <>
                                    {' '}
                                    <Td
                                        style={{ fontWeight: 'bold' }}
                                        colSpan={3}
                                    >
                                        {'Total'}
                                    </Td>
                                    <Td
                                        style={{ fontWeight: 'bold' }}
                                        colSpan={3}
                                    >
                                        {toUSD(getTotalAmount(data))}
                                    </Td>{' '}
                                </>
                            )}
                        </Tbody>
                    ) : (
                        <Tbody>
                            <Tr>
                                <Td
                                    colSpan={headers?.[0]?.headers?.length}
                                    className='px-0'
                                >
                                    {NoDataCard}
                                </Td>
                            </Tr>
                        </Tbody>
                    )}
                </React.Fragment>
            </Table>
            {data?.length > 0 && <PaginationComponents />}
        </Fragment>
    );
};

const CHAR_LIMIT = 400;

type UpdateCellProps = {
    value: string;
};

const UpdateCell = ({ value }: UpdateCellProps) => {
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    const truncateText = (text: string, characters: number) => {
        if (text.length <= characters) {
            return text;
        }
        const truncatedText = text.slice(0, characters);
        return truncatedText + '...';
    };

    const shouldDisplayButton = value.length > CHAR_LIMIT;

    return (
        <div>
            <div>
                {/* {expanded ? value : truncateText(value, CHAR_LIMIT)} */}
                {value}
                {shouldDisplayButton && (
                    <Button
                        color='link'
                        size='sm'
                        className='d-block px-0'
                        onClick={toggleExpanded}
                    >
                        {expanded ? 'See Less' : 'See More'}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default TableContainer;
